import { Button } from "../";
import FilterItem, { FilterConfig } from "./FilterItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";

// ponytail: constraint is `object` not `Record<string, unknown>` — plain caller
// interfaces (incl. frozen test fixtures) lack an index signature and fail the
// `unknown` record constraint. `object` accepts them and is not `any`.
export interface FilterBarProps<T extends object = Record<string, unknown>> {
  filters: FilterConfig[];
  values: T;
  onChange: (_key: keyof T, _value: T[keyof T]) => void;
  onReset: () => void;
  title?: string;
  layout?: "horizontal" | "grid";
  className?: string;
  showResetButton?: boolean;
}

const FilterBar = <T extends object>({
  filters,
  values,
  onChange,
  onReset,
  title,
  layout = "horizontal",
  className = "",
  showResetButton = true,
}: FilterBarProps<T>) => {
  const containerClass =
    layout === "grid"
      ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      : "flex flex-wrap items-center gap-4";

  const baseClass = title ? "mb-6 p-4 bg-zinc-900 border border-zinc-800" : "mb-6";

  return (
    <div className={`${baseClass} ${className}`}>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faFilter} className="text-zinc-500 text-xs" />
          <span className="text-xs text-zinc-500 uppercase tracking-widest font-semibold">
            {title}
          </span>
        </div>
      )}

      <div className={containerClass}>
        {filters.map((filter) => (
          <div key={filter.key} className={layout === "horizontal" ? "flex-shrink-0" : ""}>
            <FilterItem
              config={filter}
              value={values[filter.key as keyof T]}
              onChange={(value) => onChange(filter.key as keyof T, value as T[keyof T])}
              showLabel={layout === "grid"}
            />
          </div>
        ))}

        {showResetButton && layout === "horizontal" && (
          <div className="flex-shrink-0">
            <Button onClick={onReset} variant="secondary" size="md" className="w-full">
              Reset
            </Button>
          </div>
        )}
      </div>

      {showResetButton && layout === "grid" && (
        <div className="mt-3 flex justify-end">
          <Button onClick={onReset} variant="secondary" size="md">
            Reset Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
